// frontend/src/lib/groups/groupUtils.js

const MAX_DEPTH = 64; // defensive: cycle / unbounded ancestor walks throw at this depth

/**
 * Direct children of `parentId` (parentId === null returns top-level elements).
 * Preserves array order.
 */
export function getDirectChildren(elements, parentId) {
  if (!Array.isArray(elements)) return [];
  return elements.filter((el) => (el.parentId ?? null) === (parentId ?? null));
}

/**
 * All descendants (children, grandchildren, …) of `groupId`. Recursive.
 * Returns elements in DFS order. Stops if depth exceeds MAX_DEPTH (cycle guard).
 */
export function getDescendants(elements, groupId) {
  if (!Array.isArray(elements) || !groupId) return [];
  const out = [];
  const stack = [{ id: groupId, depth: 0 }];
  const seen = new Set();
  while (stack.length) {
    const { id, depth } = stack.pop();
    if (depth > MAX_DEPTH) break;
    if (seen.has(id)) continue;
    seen.add(id);
    for (const child of elements) {
      if (child.parentId === id) {
        out.push(child);
        stack.push({ id: child.id, depth: depth + 1 });
      }
    }
  }
  return out;
}

/**
 * Chain of ancestor groups for `elementId`, immediate parent first up to top.
 * Empty array if no ancestors. Bounded by MAX_DEPTH for cycle safety.
 */
export function getAncestorChain(elements, elementId) {
  const out = [];
  if (!Array.isArray(elements) || !elementId) return out;
  const byId = new Map(elements.map((el) => [el.id, el]));
  const seenIds = new Set([elementId]);
  let current = byId.get(elementId);
  let depth = 0;
  while (current && current.parentId && depth < MAX_DEPTH) {
    if (seenIds.has(current.parentId)) break; // cycle defense
    const parent = byId.get(current.parentId);
    if (!parent) break;
    out.push(parent);
    seenIds.add(parent.id);
    current = parent;
    depth += 1;
  }
  return out;
}

/**
 * The outermost ancestor of `elementId` — walks up until parentId === null
 * OR === `ancestorLimitId` (used in isolation mode: limit defines the floor).
 * Returns the element itself if it has no parent (or parent === limit).
 */
export function getOutermostAncestor(elements, elementId, ancestorLimitId = null) {
  if (!Array.isArray(elements) || !elementId) return null;
  const byId = new Map(elements.map((el) => [el.id, el]));
  let current = byId.get(elementId);
  if (!current) return null;
  let depth = 0;
  while (current && depth < MAX_DEPTH) {
    const parentId = current.parentId ?? null;
    if (parentId === ancestorLimitId) return current;
    const parent = byId.get(parentId);
    if (!parent) return current;
    current = parent;
    depth += 1;
  }
  return current;
}

/**
 * Cycle prevention: would setting `childId.parentId = proposedParentId`
 * create a cycle? Returns false if so.
 */
export function isValidParentTarget(elements, childId, proposedParentId) {
  if (!childId) return false;
  if (proposedParentId == null) return true;          // null = top-level is always valid
  if (childId === proposedParentId) return false;     // self-parent
  // Walk up from proposedParentId — if we reach childId, there's a cycle.
  const byId = new Map(elements.map((el) => [el.id, el]));
  let cursor = proposedParentId;
  const seen = new Set();
  let depth = 0;
  while (cursor && depth < MAX_DEPTH) {
    if (seen.has(cursor)) return false;               // pre-existing cycle
    if (cursor === childId) return false;             // would create cycle
    seen.add(cursor);
    const node = byId.get(cursor);
    cursor = node?.parentId ?? null;
    depth += 1;
  }
  return cursor === null;
}

/**
 * Union AABB of all visible leaf descendants of `groupId`. Reads from
 * `derivedBounds` cache when `updatedAt` is non-null; otherwise recomputes
 * and (mutably) writes the cache back onto the group element. Mutation is
 * intentional — pages are stored in React state but cache lives in a
 * separate, non-persisted field so updates don't trigger re-render.
 *
 * CACHE FRESHNESS CONTRACT (read this before calling from a drag/resize/marquee handler):
 * The cache is keyed only by `updatedAt`. Callers MUST call
 * `invalidateBoundsCacheUpward` for any element whose x/y/width/height/visible
 * mutated, OR which was reparented/added/removed, BEFORE the next call to
 * `getGroupBounds`. Failing to invalidate returns a stale, incorrect bbox.
 * Order in a handler: invalidate first → mutate via setPages → next render's
 * getGroupBounds recomputes. Invalidating after the read but before the
 * mutation also works because invalidation only nulls `updatedAt`; the next
 * read will recompute and refresh the cache.
 *
 * Returns null for empty groups (no visible descendants — includes
 * groups whose only descendants are themselves empty nested groups, and
 * groups whose visible descendants are all zero-area).
 */
export function getGroupBounds(elements, groupId) {
  if (!Array.isArray(elements) || !groupId) return null;
  const byId = new Map(elements.map((el) => [el.id, el]));
  const group = byId.get(groupId);
  if (!group) return null;

  // Cache hit
  const cached = group.derivedBounds;
  if (cached && cached.updatedAt != null) {
    return { x: cached.x, y: cached.y, width: cached.width, height: cached.height };
  }

  // Recompute from descendants
  const descendants = getDescendants(elements, groupId);
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const el of descendants) {
    if (el.type === 'group') continue;       // groups have no own bbox; their leaves contribute
    if (el.visible === false) continue;
    const x = Number(el.x) || 0;
    const y = Number(el.y) || 0;
    const w = Number(el.width) || 0;
    const h = Number(el.height) || 0;
    if (w <= 0 || h <= 0) continue;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x + w > maxX) maxX = x + w;
    if (y + h > maxY) maxY = y + h;
  }

  if (!Number.isFinite(minX) || maxX <= minX || maxY <= minY) {
    return null;
  }

  const bounds = { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  // Write cache back (mutation is OK; field is non-persisted)
  group.derivedBounds = { ...bounds, updatedAt: Date.now() };
  return bounds;
}

/**
 * Invalidate the derivedBounds cache for every ancestor group of `elementId`.
 * Call BEFORE the next `getGroupBounds` read on any ancestor whenever you
 * mutate `elementId`'s x/y/width/height/visible OR reparent/add/remove it.
 *
 * Mutates the group ancestor elements in place (sets `updatedAt = null`).
 * Element references are shared across React state updates produced by
 * `setPages(prev => prev.map(p => ({ ...p, elements: [...p.elements] })))`
 * — so invalidating against either the pre-mutation or post-mutation
 * elements array reaches the same live group objects.
 *
 * No-op if `elementId` is missing or has no ancestors. Never throws.
 */
export function invalidateBoundsCacheUpward(elements, elementId) {
  if (!Array.isArray(elements) || !elementId) return;
  for (const ancestor of getAncestorChain(elements, elementId)) {
    if (ancestor.derivedBounds) {
      ancestor.derivedBounds.updatedAt = null;
    }
  }
}

/**
 * Effective visibility/lock for `elementId`, cascading through ancestor groups.
 * visible = AND across self + every ancestor
 * locked  = OR across self + every ancestor
 *
 * Returns `{ visible: true, locked: false }` for:
 *   - missing/empty `elements`
 *   - falsy `elementId`
 *   - `elementId` not present in `elements` (stale id)
 *
 * Callers in interaction-critical paths (delete, drag, resize) MUST verify
 * the element still exists before calling — the "safe defaults" returned for
 * unknown ids will otherwise mask post-delete races.
 */
export function getEffectiveState(elements, elementId) {
  if (!Array.isArray(elements) || !elementId) return { visible: true, locked: false };
  const byId = new Map(elements.map((el) => [el.id, el]));
  const self = byId.get(elementId);
  if (!self) return { visible: true, locked: false };
  let visible = self.visible !== false;
  let locked = self.locked === true;
  for (const ancestor of getAncestorChain(elements, elementId)) {
    if (ancestor.visible === false) visible = false;
    if (ancestor.locked === true) locked = true;
  }
  return { visible, locked };
}

/**
 * Expand `selectedIds` to a flat list of leaf elements (non-group) that
 * should receive transform deltas. Selected groups → their leaf descendants.
 * Selected non-groups → themselves. Deduped by id.
 *
 * IMPORTANT — RETURNED OBJECTS ARE SNAPSHOT REFERENCES:
 * The returned array contains element objects from the `elements` snapshot
 * passed in. Do NOT mutate them in place and expect React state to update.
 * Always re-key by `.id` inside a `setPages` callback:
 *
 *   const leafIds = new Set(expandSelectionToLeaves(elements, sel).map(e => e.id));
 *   setPages(prev => prev.map(p => ({
 *     ...p,
 *     elements: p.elements.map(el => leafIds.has(el.id) ? { ...el, x: el.x + dx } : el),
 *   })));
 *
 * Unknown ids in `selectedIds` are silently skipped.
 */
export function expandSelectionToLeaves(elements, selectedIds) {
  if (!Array.isArray(elements) || !Array.isArray(selectedIds)) return [];
  const byId = new Map(elements.map((el) => [el.id, el]));
  const seen = new Set();
  const out = [];
  for (const id of selectedIds) {
    const el = byId.get(id);
    if (!el) continue;
    if (el.type === 'group') {
      for (const desc of getDescendants(elements, id)) {
        if (desc.type === 'group') continue; // groups have no own bbox
        if (seen.has(desc.id)) continue;
        seen.add(desc.id);
        out.push(desc);
      }
    } else {
      if (seen.has(el.id)) continue;
      seen.add(el.id);
      out.push(el);
    }
  }
  return out;
}

/**
 * Build a layer tree from the flat elements array. Each node: { element, children: [...] }.
 * Order preserved (top-level entries in array order; children in array order).
 * Elements with a `parentId` that doesn't exist in `elements` are promoted to top-level
 * (defensive — prevents orphaned children from disappearing).
 */
export function buildLayerTree(elements) {
  if (!Array.isArray(elements)) return [];
  const byId = new Map(elements.map((el) => [el.id, { element: el, children: [] }]));
  const roots = [];
  for (const el of elements) {
    const node = byId.get(el.id);
    if (el.parentId && byId.has(el.parentId)) {
      byId.get(el.parentId).children.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
}

/**
 * Compute resize updates for a list of leaf descendants given the group's
 * pre-resize bounds and per-axis scale factors. Returns a Map<id, patch>.
 *
 * The caller (CanvasEditor) iterates the map and calls onUpdateElement(id, patch)
 * for each entry.
 *
 * Scales:
 *   x = bounds.x + (oldX - bounds.x) * scaleX
 *   y = bounds.y + (oldY - bounds.y) * scaleY
 *   width = oldWidth * scaleX
 *   height = oldHeight * scaleY
 *   fontSize = oldFontSize * scaleY
 *   strokeWidth = oldStrokeWidth * min(scaleX, scaleY)   // isotropic strokes
 *   cornerRadius = oldCornerRadius * min(scaleX, scaleY) // isotropic corners
 *
 * Skips groups (no own bbox) and elements without numeric x/y.
 * Rotation is NOT touched (single-element resize doesn't change rotation either).
 * Returns an empty Map on invalid inputs (non-array descendants, missing bounds,
 * non-finite scale factors).
 */
export function applyResizeToDescendants(descendants, bounds, scaleX, scaleY) {
  const out = new Map();
  if (!Array.isArray(descendants) || !bounds) return out;
  const sx = Number(scaleX);
  const sy = Number(scaleY);
  if (!Number.isFinite(sx) || !Number.isFinite(sy)) return out;
  const isoScale = Math.min(Math.abs(sx), Math.abs(sy));

  for (const el of descendants) {
    if (el.type === 'group') continue;
    if (typeof el.x !== 'number' || typeof el.y !== 'number') continue;

    const patch = {
      x: bounds.x + (el.x - bounds.x) * sx,
      y: bounds.y + (el.y - bounds.y) * sy,
      width: (Number(el.width) || 0) * sx,
      height: (Number(el.height) || 0) * sy,
    };
    if (typeof el.fontSize === 'number') {
      patch.fontSize = el.fontSize * sy;
    }
    if (typeof el.strokeWidth === 'number') {
      patch.strokeWidth = el.strokeWidth * isoScale;
    }
    if (typeof el.cornerRadius === 'number') {
      patch.cornerRadius = el.cornerRadius * isoScale;
    }
    out.set(el.id, patch);
  }
  return out;
}

/**
 * Compute rotate updates for a list of leaf descendants given the group's
 * bounds and a rotation delta (degrees, CW positive). Returns a Map<id, patch>.
 *
 * Each descendant is rotated around the group bounds center; the descendant's
 * own `rotation` field is incremented by the same delta so its visual
 * orientation matches its new position.
 *
 * Skips groups (no own bbox) and elements without numeric x/y.
 * Defensive: returns empty Map for non-array descendants, missing bounds,
 * or non-finite delta.
 */
export function applyRotateToDescendants(descendants, bounds, deltaDegrees) {
  const out = new Map();
  if (!Array.isArray(descendants) || !bounds) return out;
  const delta = Number(deltaDegrees) || 0;
  const rad = delta * Math.PI / 180;
  if (!Number.isFinite(rad)) return out;
  const cx = bounds.x + bounds.width / 2;
  const cy = bounds.y + bounds.height / 2;
  const cosT = Math.cos(rad);
  const sinT = Math.sin(rad);

  for (const el of descendants) {
    if (el.type === 'group') continue;
    if (typeof el.x !== 'number' || typeof el.y !== 'number') continue;
    const w = Number(el.width) || 0;
    const h = Number(el.height) || 0;
    const elCenterX = el.x + w / 2;
    const elCenterY = el.y + h / 2;
    const dx = elCenterX - cx;
    const dy = elCenterY - cy;
    const newCenterX = cx + dx * cosT - dy * sinT;
    const newCenterY = cy + dx * sinT + dy * cosT;
    const patch = {
      x: newCenterX - w / 2,
      y: newCenterY - h / 2,
      rotation: (Number(el.rotation) || 0) + delta,
    };
    out.set(el.id, patch);
  }
  return out;
}
